import { Slot } from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes } from 'react';

import { classNames } from '../utils/class-names';
import styles from './button.component.module.css';

type Variant = 'primary' | 'secondary' | 'subtle' | 'ghost';
type Size = 'small' | 'medium' | 'large';
type Tone = 'default' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: Variant;
	size?: Size;
	/** Semantic tone, composable with any variant (e.g. a danger ghost). */
	tone?: Tone;
	/** Render as the single child element (Radix Slot) instead of a <button>. */
	asChild?: boolean;
}

/**
 * Base button. Radix `Slot` powers `asChild` (e.g. wrap an <a> while keeping
 * button styling); variants/size are typed props surfaced as data attributes
 * for CSS to target.
 */
export function Button({
	variant = 'primary',
	size = 'medium',
	tone = 'default',
	asChild = false,
	className,
	type,
	...props
}: ButtonProps) {
	const Component = asChild ? Slot : 'button';
	return (
		<Component
			className={classNames(styles.button, className)}
			data-variant={variant}
			data-size={size}
			data-tone={tone === 'default' ? undefined : tone}
			// A native <button> defaults to type=submit; force "button" unless a
			// caller overrides. When asChild, the rendered child owns its semantics.
			type={asChild ? type : (type ?? 'button')}
			{...props}
		/>
	);
}
